import * as d3 from "d3";
import { MouseEventHandler, useEffect, useState } from "react";

import { ActionTypeFromPlayer, SelectActionMenu } from "../components";
import { highlightAssociatedStencils } from "../components/stencil-wrapper/utils";
import { usePlayContext } from "../pages";
import { Sport } from "../redux/slices";
import {
  Action,
  ActionType,
  BasketballActionType,
  FrameStencil,
  FrameStencils,
  HockeyActionType,
  MidPoint,
  PointType,
  StencilType,
} from "../types";
import { SoccerActionLineType } from "../types/frame-stencil/soccer";
import { createNewAction, distanceBetweenPoints, findClosestStencil, getPlayerMarkers } from "../utils";
import { useGetFreeDrawPoints } from "./use-get-free-draw-points";

const TIME_FOR_ANGLE_CUT = 500; // ms

/** Interpret the allowable  */
const getActionTypeFromPlayer = (action: Action, frameStencils: FrameStencil[]): ActionTypeFromPlayer => {
  const playersWithoutActions = getPlayerMarkers(frameStencils).filter((player) => player.movements.length === 0);

  const closestPlayer = findClosestStencil({
    target: { id: action.id, point: action.pos },
    stencils: playersWithoutActions,
  });

  if (!closestPlayer) return null;

  return closestPlayer.possession ? "PLAYER_WITH_POSSESSION" : "PLAYER_WITHOUT_POSSESSION";
};

export type UseCourtFreeDrawProps = {
  freeDraw: boolean;
  openTooltip: (menu: SelectActionMenu) => void;
  frameStencils: FrameStencils;
  closeTooltip: () => void;
};

export const useCourtFreeDraw = ({ freeDraw, openTooltip, frameStencils, closeTooltip }: UseCourtFreeDrawProps) => {
  const { playStateApi } = usePlayContext();
  const { sport } = playStateApi.playState;
  const { getFreeDrawPoints } = useGetFreeDrawPoints();

  const [drawing, setDrawing] = useState(false);
  const [freeDrawPoints, setFreeDrawPoints] = useState<MidPoint[]>([]);
  const [smoothFreeDrawPoints, setSmoothFreeDrawPoints] = useState<MidPoint[]>([]);
  const [lastSegmentIndex, setLastSegmentIndex] = useState<number | null>(null);
  const [lastMovementTime, setLastMovementTime] = useState<number | null>(null);

  const onFreeDraw: MouseEventHandler<SVGSVGElement> = (event) => {
    if (drawing) {
      closeTooltip();
      const x = d3.pointer(event)[0];
      const y = d3.pointer(event)[1];
      const newFreeDrawPoint = {
        x,
        y,
        pointType: PointType.CURVE,
      };
      const shiftHeld = event.shiftKey;

      const currentTime = Date.now();
      // if it's been more than TIME_FOR_ANGLE_CUT ms since the last movement, add an angle cut
      if (lastMovementTime && currentTime - lastMovementTime > TIME_FOR_ANGLE_CUT) {
        // start a new segment
        // set segment index to the current length of smoothFreeDrawPoints so we know what we've drawn so far
        setLastSegmentIndex(smoothFreeDrawPoints.length - 1);

        // set the last point of smoothFreeDrawPoints to an angle
        setSmoothFreeDrawPoints((smoothFreeDrawPoints) => {
          return smoothFreeDrawPoints.map((point, index) => ({
            ...point,
            pointType: index === smoothFreeDrawPoints.length - 1 ? PointType.ANGLE : point.pointType,
          }));
        });

        setFreeDrawPoints([]);
      } else {
        setFreeDrawPoints((freeDrawPoints) => {
          const newFreeDrawPoints: MidPoint[] = [...freeDrawPoints, newFreeDrawPoint];

          if (newFreeDrawPoints.length > 1) {
            const newSmoothFreeDrawPoints = getFreeDrawPoints({
              tracePoints: newFreeDrawPoints,
              shiftHeld,
            }) as MidPoint[];

            if (lastSegmentIndex === null) {
              setSmoothFreeDrawPoints(newSmoothFreeDrawPoints);
            } else {
              setSmoothFreeDrawPoints((smoothFreeDrawPoints) => {
                // remove all elements after the last segment index
                smoothFreeDrawPoints.splice(lastSegmentIndex + 1);

                // remove the first point of newSmoothFreeDrawPointsCopy
                newSmoothFreeDrawPoints.shift();

                return [...smoothFreeDrawPoints, ...newSmoothFreeDrawPoints];
              });
            }
          }

          return newFreeDrawPoints;
        });
      }

      setLastMovementTime(currentTime);
    }
    // if we're in free draw mode, highlight associated stencils
    if (freeDraw) {
      const x = d3.pointer(event)[0];
      const y = d3.pointer(event)[1];

      // highlight player markers
      const markers = getPlayerMarkers(frameStencils);
      // highlight Cut, Dribble, and Screen actions
      const actions = frameStencils
        .filter((stencil): stencil is Action => stencil.stencilType === StencilType.ACTION)
        .filter((action) => [ActionType.PLAYER, ActionType.POSSESSION].includes(action.actionType));

      highlightAssociatedStencils({
        newX: x,
        newY: y,
        start: [...markers, ...actions],
        end: [],
        stencilType: StencilType.ACTION,
        actionRelativeEndPosition: { x: 0, y: 0 },
      });
    }
  };

  const getCutActionFromSport = (sport: Sport): Action["layerType"] => {
    switch (sport) {
      case Sport.BASKETBALL:
        return BasketballActionType.CUT;

      case Sport.HOCKEY:
        return HockeyActionType.SKATE;

      case Sport.SOCCER:
        return SoccerActionLineType.SC_RUN;

      case Sport.NO_SPORT:
        throw new Error(`Invalid sport: ${sport}`);
    }
  };

  const onFreeDrawEnd: MouseEventHandler<SVGSVGElement> = (e) => {
    if (drawing) {
      setDrawing(false);

      // set play drawing data
      if (smoothFreeDrawPoints.length > 1) {
        const trimmedFreeDrawPoints = [...smoothFreeDrawPoints];

        if (trimmedFreeDrawPoints.length > 2) {
          // See if the last segment is tiny and remove it.
          const lastPoint = trimmedFreeDrawPoints[trimmedFreeDrawPoints.length - 1] as MidPoint;
          const penultimatePoint = trimmedFreeDrawPoints[trimmedFreeDrawPoints.length - 2] as MidPoint;

          const distance = distanceBetweenPoints(lastPoint.x, lastPoint.y, penultimatePoint.x, penultimatePoint.y);

          if (distance < 4 && penultimatePoint.pointType !== PointType.CURVE) {
            trimmedFreeDrawPoints.pop();
          }
        }
        // build action here
        const startX = (trimmedFreeDrawPoints[0] as MidPoint).x;
        const startY = (trimmedFreeDrawPoints[0] as MidPoint).y;
        const endX = (trimmedFreeDrawPoints[trimmedFreeDrawPoints.length - 1] as MidPoint).x;
        const endY = (trimmedFreeDrawPoints[trimmedFreeDrawPoints.length - 1] as MidPoint).y;
        const midPoints = trimmedFreeDrawPoints.slice(1, trimmedFreeDrawPoints.length - 1);
        const action = createNewAction({
          layerType: getCutActionFromSport(sport),
          pos: { x: startX, y: startY },
          midPoints,
          endPos: { x: endX, y: endY },
        });

        const actionTypeFromPlayer = getActionTypeFromPlayer(action, frameStencils);

        // show stencil variation tooltip
        openTooltip({
          menuPos: { x: e.pageX, y: e.pageY },
          action,
          actionTypeFromPlayer,
          cleanup: () => {
            setLastSegmentIndex(null);
            setFreeDrawPoints([]);
            setSmoothFreeDrawPoints([]);
            setLastMovementTime(null);
          },
        });
      }
    }
  };

  const onFreeDrawStart: MouseEventHandler<SVGSVGElement> = (event) => {
    if (event.button === 0 && freeDraw) {
      setDrawing(true);
    }
  };

  // if freeDraw is turned off, reset the state
  useEffect(() => {
    if (drawing && !freeDraw) {
      setDrawing(false);
      setFreeDrawPoints([]);
      setSmoothFreeDrawPoints([]);
      setLastSegmentIndex(null);
      setLastMovementTime(null);
    }
  }, [drawing, freeDraw]);

  return { onFreeDraw, onFreeDrawEnd, onFreeDrawStart, smoothFreeDrawPoints, freeDrawPoints };
};
